import * as dateFnsCompat from 'utils/date-fns-compat';

import fp from 'lodash/fp';
import React from 'react';
import { Breadcrumb, BreadcrumbItem, Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { cpdActivityMap, dateFormats, routes } from '../../../../constants';
import { getUserIndustryGoals, isBusinessAdmin, startOfNextCpdPeriod, startOfThisCpdPeriod } from '../../../../services';
import { getCpdActivityColumns, mapCpdActivity } from '../../../../services/cpd-activity';
import { businessActions, businessSelector, currentTeamMembersSelector, teamMembersSelector } from '../../../../state/modules/business';
import { currentUserCpdDaysRemaining, currentUserSelector } from '../../../../state/modules/user';
import { downloadBlobStreamFile, getBlobStream, getCsvStream, possessiveCase, writeDataToCsvStream } from '../../../../utils';
import { DateSelect, Icon } from '../../../components';
import Loading from '../../loading';
import { CpdActivityTable } from '../professional-profile/components';

const TeamUserCpdActivity = ({
  currentUser,
  cpdActivity,
  onDateRangeChange,
  loadMore,
  sortBy,
  filter,
  cpdActivityHits,
  rejectText,
  fullName,
  contextUserId,
  contextBusinessTeamId,
  exportCsv
}) => (
    <div className='base-layout'>
      <section id='header--profile' key='cpd-activity-header' className='layout--header'>
        <Grid>
          <Row className='sm-gutter'>
            <Col xs={12} md={5}>
              <Breadcrumb>
                <LinkContainer to={contextBusinessTeamId ? routes.app.teams.teamMemberProfiles(contextBusinessTeamId, contextUserId) : routes.app.teams.userProfiles(contextUserId)}>
                  <BreadcrumbItem>
                    {possessiveCase(fullName)} Professional profile
                  </BreadcrumbItem>
                </LinkContainer>
                <BreadcrumbItem active>
                  {possessiveCase(fullName)} CPD Activity
                </BreadcrumbItem>
              </Breadcrumb>
            </Col>
          </Row>
          <Row className='button-gutter'>
            <Col xs={12} md={5}>
              <h1>
                <Icon icon='cpd-activity-large' /> CPD Activity
              </h1>
              <hr />
            </Col>
            <Col xs={12} md={2} mdOffset={5}>
              <Button
                className='large full pull-down'
                onClick={exportCsv}>
                <Icon iconSet='glyphicon' icon='download-alt' className="space-right" /> export to csv
              </Button>
            </Col>
          </Row>
        </Grid>
      </section>
      <div id='section--cpd-activity' key='cpd-activity-section' className='layout--section'>
        <Grid>
          <Row className='no-gutter sectionHeader'>
            <form>
              <table className='table--date-search'>
                <tbody>
                  <tr>
                    <td>
                      Show activity between
                    </td>
                    <td>
                      <DateSelect date={filter.fromDate} onChange={(date) => onDateRangeChange('fromDate', date)} />
                    </td>
                    <td>
                      and
                    </td>
                    <td>
                      <DateSelect date={filter.toDate} onChange={(date) => onDateRangeChange('toDate', date)} futureYears={1} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </form>
          </Row>
          <CpdActivityTable
            className='table--cpd-activity'
            cpdActivity={cpdActivity}
            cpdActivityMap={cpdActivityMap(rejectText, isBusinessAdmin(currentUser) !== undefined, { review: true, userId: contextUserId })}
            sortBy={sortBy}
          />
          {
            cpdActivity.length < cpdActivityHits && cpdActivity.length > 0 ?
              <Row className='no-gutter row-slider'>
                <Col xs={12} md={4} mdOffset={4}>
                  <Button
                    className='large full'
                    onClick={loadMore}>
                    Load more CPD activity
                  </Button>
                </Col>
              </Row> :
              null
          }
        </Grid>
      </div>
    </div>
  );

const mapStateToProps = (state) => ({
  currentUser: currentUserSelector(state),
  allTeamMembers: teamMembersSelector(state),
  currentTeamMembers: currentTeamMembersSelector(state),
  cpdActivity: state.business.cpdActivities ? state.business.cpdActivities.userCpdPoints : [],
  cpdActivityHits: state.business.cpdActivities && state.business.cpdActivities.hits,
  business: businessSelector(state),
  cpdDaysRemaining: currentUserCpdDaysRemaining(state),
  industryCpdCategorys: state.industry.cpdCategorys,
  publishers: state.publisher.publishers,
  userIndustryGoals: getUserIndustryGoals(state.user.profile, fp.get('industriesWithCpdCategorys')(state.industry))
});

const mapDispatchToProps = (dispatch) => ({
  getTeam: bindActionCreators(businessActions.business.team.getTeam, dispatch),
  getCpdPoints: bindActionCreators(businessActions.business.user.getCpdActivities, dispatch),
  loadMoreCpdPoints: bindActionCreators(businessActions.business.user.loadMore, dispatch)
});

export default compose(
  withProps(({
    match: {
      params: {
        team,
        user
      }
    }
  }) => ({
    contextBusinessTeamId: team,
    contextUserId: user
  })),
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    ({ cpdDaysRemaining, industryCpdCategorys }) => ({
      filter: {
        skip: 0,
        fromDate: startOfThisCpdPeriod(cpdDaysRemaining),
        toDate: startOfNextCpdPeriod(cpdDaysRemaining),
        sort: '',
        cpdCategorys: fp.map('id')(industryCpdCategorys).join(',')
      }
    }),
    {
      onQuerying: () => (filter) => ({ filter })
    }
  ),
  withProps(() => ({
    rejectText: "(draft)"
  })),
  branch(
    ({ business }) => !business,
    renderComponent(Loading)
  ),
  lifecycle({
    componentDidMount() {
      const {
        getCpdPoints,
        getTeam,
        business,
        contextBusinessTeamId,
        contextUserId,
        filter
      } = this.props;

      getCpdPoints(business.id, contextUserId, filter);

      if (contextBusinessTeamId) {
        getTeam(business.id, contextBusinessTeamId)
      }
    }
  }),
  withProps(({
    currentUser,
    allTeamMembers,
    currentTeamMembers,
    contextUserId
  }) => ({
    user: fp.flow(
      fp.filter(({ user: { id } }) => id === parseInt(contextUserId, 10)),
      fp.map(({ user }) => user),
      fp.first
    )(isBusinessAdmin(currentUser) ? allTeamMembers : currentTeamMembers)
  })),
  withHandlers({
    sortBy: ({ getCpdPoints, user, business, filter, onSort, onQuerying }) => (field, sortOrder) => {
      const sortParam = sortOrder ? `${field},${sortOrder}` : undefined;
      const nextFilter = { ...filter, sort: sortParam, skip: 0 };
      onQuerying(nextFilter);
      getCpdPoints(business.id, user.id, nextFilter);
    },
    loadMore: ({ business, user, loadMoreCpdPoints, filter, cpdActivity, onQuerying }) => () => {
      const nextFilter = { ...filter, skip: cpdActivity.length };
      onQuerying(nextFilter);
      loadMoreCpdPoints(business.id, user.id, nextFilter);
    },
    onDateRangeChange: ({ getCpdPoints, user, business, filter, onQuerying, setDateRange }) => (key, date) => {
      const nextFilter = { ...filter, [key]: key === 'toDate' ? dateFnsCompat.format(dateFnsCompat.endOfDay(date)) : dateFnsCompat.format(date), skip: 0 };
      onQuerying(nextFilter);
      if (nextFilter.fromDate && nextFilter.toDate) {
        getCpdPoints(business.id, user.id, nextFilter);
      }
    }
  }),
  branch(
    ({ user }) => !user,
    renderComponent(Loading)
  ),
  withProps(
    ({
      user: {
        firstName,
        lastName
      }
    }) => ({
      fullName: `${firstName} ${lastName}`
    })
  ),
  withHandlers({
    exportCsv: ({ cpdActivity, industryCpdCategorys, publishers, filter, user, userIndustryGoals }) => () => {
      const columns = getCpdActivityColumns(industryCpdCategorys, userIndustryGoals)
      const rows = mapCpdActivity(industryCpdCategorys, publishers, userIndustryGoals)(
        fp.flow(
          fp.uniqBy(
            ({ userCpdPointId, cpdCategoryId }) => ({ userCpdPointId, cpdCategoryId })
          ),
          fp.sortBy(
            ({ timeEarned }) => {
              return -1 * dateFnsCompat.parse(timeEarned).getTime()
            }
          )
        )(cpdActivity)
      )
      const {
        firstName,
        lastName
      } = user
      const endDate = dateFnsCompat.format(dateFnsCompat.parse(filter.toDate), dateFormats.ShortDate).replace(/\//g, '-')
      downloadBlobStreamFile(
        writeDataToCsvStream(
          getCsvStream({
            header: true,
            columns
          }),
          rows
        ).pipe(
          getBlobStream()
        ),
        `${firstName}-${lastName}-CPD-activity-${endDate}.csv`
      );
    }
  })
)(TeamUserCpdActivity);



// WEBPACK FOOTER //
// ./src/containers/pages/app/teams/user-cpd-activity.js